import router from "@/router";
import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import currenciesSymbols from "@/countries/currenciesSymbols";

import Utils from "@/utils/Utils";
import { debugLogRef } from "@/utils/debugLog";

const UsersRepository = RepositoryFactory.get("users");
const TokensRepository = RepositoryFactory.get("tokens");

const state = {
  user: {
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    groups: [],
    subscription_expiry: null,
    hide_subscription_reminder: false,
    teacher: null,
    student_id: null,
    commercial: false,
  },
  // null: loading hasn't started ; true: loading ongoing ; false: loading has ended
  loadingUserData: null,
  user_country: {
    country: {},
  },
};

const getters = {
  isAdmin: (state) => {
    return state.user.groups.includes("admin");
  },
  isAdminTabuleo: (state) => {
    return state.user.groups.includes("admin_tabuleo");
  },
  isTeacher: (state) => {
    return state.user.groups.includes("teacher");
  },
  isStudent: (state) => {
    return state.user.groups.includes("student");
  },
  isLogged: (state) => {
    return state.user.username !== "";
  },
  isSubscriptionPaid: (state) => {
    return (
      state.user.username.length > 0 && !state.user.groups.includes("demo")
    );
  },
  teacherGradingSystem: (state) => {
    if (state.user.teacher && state.user.teacher.grading_system) {
      return state.user.teacher.grading_system;
    }

    return null;
  },
  getCountry: (state) => {
    return state.user_country;
  },
  getUserFullname: (state) => {
    return `${state.user.first_name} ${state.user.last_name}`;
  },
  getLanguageUsed: (state) => {
    return state.user.language_used;
  },
};

const actions = {
  createUser({ dispatch, state }, user) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    user.language_used = state.getLocale();
    return UsersRepository.createUser(user);
  },
  changeUserPassword({ dispatch, state }, payload) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return UsersRepository.changePassword(state.user.username, payload)
      .then((response) => {
        if (response.data && response.data.two_factor_auth === true) {
          // Encrypt & store form data
          let encryptedPayload = Utils.aesEncrypt(payload);
          Utils.setCookie("2fa-form-data", encryptedPayload, 1);
          Utils.setCookie("2fa-user-email", state.user.email, 1);
          Utils.setCookie("2fa-action", "users/changeUserPassword", 1);

          return Promise.resolve({ redirectTo: "TwoFactorAuthentication" });
        } else {
          dispatch(
            "notifications/addSuccess",
            {
              message: state.$t(`message.notifications.passwordChanged`),
            },
            { root: true },
          );
        }
      })
      .catch((error) => {
        let message = "";

        if (
          error.response.status == 400 &&
          error.response.data.details === "Invalid old password"
        ) {
          message = state.$t(`message.notifications.invalidOldPassword`);
        } else if (
          error.response.status == 400 &&
          error.response.data.details ===
            "New password & confirmation don't match"
        ) {
          message = state.$t(
            `message.notifications.passwordConfirmationDontMatch`,
          );
        } else {
          message = state.$t(
            `message.notifications.passwordConfirmationDontMatch`,
          );
        }
        dispatch(
          "notifications/addError",
          {
            message: message,
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  updateLastLogin({ state }) {
    return UsersRepository.updateLastLogin(state.user.username).catch(
      (error) => {
        console.log("Error updating last login", state.user.username, error);
      },
    );
  },
  getUser({ dispatch, commit }, payload) {
    const username = typeof payload === "string" ? payload : payload.username;
    const needSession =
      typeof payload === "string" ? true : payload.needSession !== false;
    debugLogRef(
      "SESSION UNIQUE",
      "getUser: needSession =",
      needSession,
      payload,
      typeof payload,
      payload.needSession,
    );

    commit("START_LOADING_USER_DATA");
    return UsersRepository.getUser(username, { needSession: needSession })
      .then((response) => {
        const user = response.data;
        const sessionInfo = user.active_session;

        // delete key to avoid storing unnecessary data in the user state
        delete user.active_session;

        if (user.groups.includes("student")) {
          dispatch("sessions/startSession", sessionInfo, { root: true });
        }

        // Store user data
        commit("SAVE_USER", user);
      })
      .catch(function (error) {
        if (error.response) {
          if (error.response.status == 410) {
            // In case where user already disconnected by backend
            dispatch(
              "notifications/addError",
              {
                message: state.$t(`message.notifications.user.loginTimeout`),
              },
              { root: true },
            );
            dispatch("logout");
            router.push({ name: "HomePage" });
            dispatch(
              "notifications/addSuccess",
              {
                message: state.$t(`message.logout`),
              },
              { root: true },
            );
          } else {
            dispatch(
              "notifications/addError",
              {
                message: state.$t(
                  `message.notifications.user.loadingDataError`,
                ),
              },
              { root: true },
            );
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          dispatch(
            "notifications/addError",
            {
              message: state.$t(`message.notifications.user.loadingDataError`),
            },
            { root: true },
          );
        }
      })
      .finally(() => {
        commit("END_LOADING_USER_DATA");
      });
  },
  editUser({ commit, dispatch, state }, form) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    const payload = {
      first_name: form.first_name,
      last_name: form.last_name,
      commercial: form.commercial,
    };
    UsersRepository.editUser(state.user.username, payload)
      .then((response) => {
        const user = response.data;
        commit("SAVE_USER", user);
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.user.editionSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.user.editionFailure`),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  deleteUser({ dispatch }, username) {
    UsersRepository.deleteUser(username)
      .then(() => {
        dispatch("logout");
        router.push({ name: "HomePage" });
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.user.deleteSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.account.deleteFailure`),
          },
          { root: true },
        );
      });
  },
  deleteUserFromAdmin({ dispatch }, userdata) {
    UsersRepository.deleteUser(userdata.username)
      .then(() => {
        if (userdata.usertype == "student") {
          router.push({ name: "AdminStudents" });
        } else if (userdata.usertype == "teacher") {
          router.push({ name: "AdminTeachers" });
        } else {
          router.push({ name: "PersonalHome" });
        }
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.account.deleteSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.account.deleteFailure`),
          },
          { root: true },
        );
      });
  },
  login({ commit, dispatch, state }, credentials) {
    const payload = {
      username: credentials.email,
      password: credentials.password,
    };

    dispatch("loadings/enableLoadings", {}, { root: true });
    commit("START_LOADING_USER_DATA");
    return TokensRepository.getAccessToken(payload)
      .then((response) => {
        if (response.data && response.data.two_factor_auth === true) {
          // Encrypt & store form data
          let encryptedPayload = Utils.aesEncrypt(credentials);
          Utils.setCookie("2fa-form-data", encryptedPayload, 1);
          Utils.setCookie("2fa-user-email", credentials.email, 1);
          Utils.setCookie("2fa-action", "users/login", 1);

          return Promise.resolve({ redirectTo: "TwoFactorAuthentication" });
        } else {
          const tokens = response.data;
          const accessToken = tokens.access;
          const refreshToken = tokens.refresh;
          const accessTokenPayload = Utils.getJWTPayload(accessToken);

          localStorage.setItem("iizenglish-access-token", accessToken);
          localStorage.setItem("iizenglish-refresh-token", refreshToken);

          return UsersRepository.getUser(accessTokenPayload.username).then(
            (response) => {
              const user = response.data;
              const sessionInfo = user.active_session;
              delete user.active_session;

              commit("SAVE_USER", user);
              // Initialiser la session si étudiant
              if (user.groups.includes("student")) {
                dispatch("sessions/startSession", sessionInfo, { root: true });
              }
            },
          );
        }
      })
      .catch((error) => {
        dispatch("loadings/disableLoadings", {}, { root: true });
        let notificationMessage = "";

        if (error.response.status === 401) {
          notificationMessage = state.$t(
            `message.notifications.emailOrPasswordWrong`,
          );
        } else if (
          error.response.status === 400 &&
          error.response.data.admin_only
        ) {
          notificationMessage = state.$t(
            `message.notifications.loginNotAllowed`,
          );
        } else {
          notificationMessage = state.$t(`message.notifications.loginError`);
        }

        dispatch(
          "notifications/addError",
          {
            message: notificationMessage,
          },
          { root: true },
        );
      })
      .finally(() => {
        commit("END_LOADING_USER_DATA");
      });
  },
  logout({ commit, dispatch }) {
    dispatch("sessions/clearSession", {}, { root: true });

    localStorage.removeItem("iizenglish-access-token");
    localStorage.removeItem("iizenglish-refresh-token");
    const user = {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      groups: [],
      subscription_expiry: null,
    };
    commit("SAVE_USER", user);
    commit("teachers/RESET_TEACHER_STUDENTS", null, { root: true });
  },
  validateAccount({ dispatch }, token) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return TokensRepository.validateAccount(token);
  },
  resetEmailValidationToken({ dispatch }, user) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return TokensRepository.resetEmailValidationToken(user);
  },
  resetPassword({ dispatch }, email) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return TokensRepository.resetPassword(email);
  },
  changePassword({ dispatch }, passwordData) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return TokensRepository.changePassword(passwordData);
  },
  sendEmail({ dispatch }, payload) {
    const username = payload.username;
    delete payload.username;

    UsersRepository.sendEmail(username, payload)
      .then(() => {
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.sendReminderSuccess`),
          },
          { root: true },
        );
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.sendReminderError`),
          },
          { root: true },
        );
      });
  },
  setPassword(_, passwordData) {
    return TokensRepository.setPassword(passwordData);
  },
  updateUser({ commit }, user) {
    commit("SAVE_USER", user);
  },
  verifyToken({ dispatch }, tokenData) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return TokensRepository.verifyToken(tokenData);
  },
  getLocalization({ commit }) {
    return UsersRepository.getLocalization().then((response) => {
      let country = response.data;
      country.currency = currenciesSymbols[country.currency];
      commit("SET_USER_COUNTRY", country);
    });
  },
  getUserPersonalData({ dispatch, commit }, username) {
    commit("START_LOADING_USER_DATA");
    return UsersRepository.getUserPersonalData(username)
      .then((response) => {
        const userdata = response.data;
        Utils.download(`personal_data_${username}.csv`, userdata);
      })
      .catch(function (error) {
        if (error.response) {
          if (error.response.status == 410) {
            // In case where user already disconnected by backend
            dispatch(
              "notifications/addError",
              {
                message: state.$t(`message.notifications.user.loginTimeout`),
              },
              { root: true },
            );
            dispatch("logout");
            router.push({ name: "HomePage" });
            dispatch(
              "notifications/addSuccess",
              {
                message: state.$t(`message.logout`),
              },
              { root: true },
            );
          } else {
            dispatch(
              "notifications/addError",
              {
                message: state.$t(
                  `message.notifications.user.loadingDataError`,
                ),
              },
              { root: true },
            );
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          dispatch(
            "notifications/addError",
            {
              message: state.$t(`message.notifications.user.loadingDataError`),
            },
            { root: true },
          );
        }
      })
      .finally(() => {
        commit("END_LOADING_USER_DATA");
      });
  },
  verifyOtp({ dispatch, commit }, payload) {
    commit("START_LOADING_USER_DATA");
    return TokensRepository.verifyOtp(payload)
      .then(() => {
        // 2 factor auth complete. Identity verified server-side. Restart action.
        const action = Utils.getCookie("2fa-action");
        const formData = Utils.aesDecrypt(Utils.getCookie("2fa-form-data"));

        // Delete 2fa related cookies
        Utils.deleteCookie("2fa-action");
        Utils.deleteCookie("2fa-form-data");
        Utils.deleteCookie("2fa-user-email");

        return dispatch(action, formData, { root: true });
      })
      .catch((error) => {
        let notificationMessage = "";

        if (error.response.status === 404) {
          notificationMessage = state.$t(
            `message.notifications.twoFactorAuth.incorrectCode`,
          );
        } else if (error.response.status === 410) {
          notificationMessage = state.$t(
            `message.notifications.twoFactorAuth.expiredCode`,
          );
        } else {
          notificationMessage = state.$t(
            `message.notifications.twoFactorAuth.validationError`,
          );
        }

        dispatch(
          "notifications/addError",
          {
            message: notificationMessage,
          },
          { root: true },
        );
        throw new Error();
      })
      .finally(() => {
        commit("END_LOADING_USER_DATA");
      });
  },
  ssoToken({ commit, dispatch }, token) {
    let payload = {
      sso_token: token,
    };
    return TokensRepository.ssoToken(payload).then((response) => {
      const tokens = response.data;
      const accessToken = tokens.access;
      const refreshToken = tokens.refresh;
      const accessTokenPayload = Utils.getJWTPayload(accessToken);

      localStorage.setItem("iizenglish-access-token", accessToken);
      localStorage.setItem("iizenglish-refresh-token", refreshToken);

      return UsersRepository.getUser(accessTokenPayload.username).then(
        (response) => {
          const user = response.data;
          const sessionInfo = user.active_session;
          delete user.active_session;
          commit("SAVE_USER", user);

          if (user.groups.includes("student")) {
            return dispatch("sessions/startSession", sessionInfo, {
              root: true,
            });
          }
        },
      );
    });
  },
  updateLocale({ state }) {
    let params = {
      language_used: state.getLocale(),
    };
    UsersRepository.updateLocale(state.user.username, params);
  },
};

const mutations = {
  SAVE_USER(state, user) {
    state.user = user;
  },
  START_LOADING_USER_DATA(state) {
    state.loadingUserData = true;
  },
  END_LOADING_USER_DATA(state) {
    state.loadingUserData = false;
  },
  SET_USER_COUNTRY(state, country) {
    state.user_country = country;
  },
  SET_USER_HIDE_SUBSCRIPTION_REMINDER(state) {
    state.user.hide_subscription_reminder = true;
  },
  SET_TEACHER_GRADING_SYSTEM(state, gradingSystem) {
    if (state.user.teacher !== null) {
      state.user.teacher.grading_system = gradingSystem;
    }
  },
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
